/**
 * 게이트 열기 버튼 (출석부 페이지 상단)
 *
 * 여는 길이 두 갈래다 (2026-08-26):
 *  ① 서버 경유 — POST /v1/gate/remote-open. 로그인한 계정의 매장 게이트만 열린다.
 *     어디서든 되고(집·본부), HTTPS 페이지에서도 막히지 않는다.
 *  ② 직접 호출 — http://{ESP32}/open. 매장 안 PC 에서 서버가 죽어도 열린다.
 *
 * 🔴 ①을 먼저 쓴다. HTTPS 페이지(app/stg.newattend.com)에서 http://192.168.x.x 를 부르면
 *    브라우저가 mixed content 로 차단해 ②는 아예 나가지도 못한다 — 로컬 매장(HTTP)에서만 유효.
 * 🔴 게이트가 잠긴 매장은 ②에도 매장 열쇠(X-Gate-Token)를 실어야 열린다.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../../infrastructure/api';

interface GateIpSettings {
  organization_id: string;
  esp32_entry_ip?: string | null;
  esp32_exit_ip?: string | null;
  esp32_open_duration_ms: number;
}

const GateOpenButton: React.FC = () => {
  const [settings, setSettings] = useState<GateIpSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [opening, setOpening] = useState<'entry' | 'exit' | null>(null);
  const [editEntry, setEditEntry] = useState('');
  const [editExit, setEditExit] = useState('');
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanFound, setScanFound] = useState<string[]>([]);
  const [gateToken, setGateToken] = useState<string>('');   // 잠긴 게이트를 직접 부를 때 필요

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/v1/owner/gate-ip');
      setSettings(res.data);
      setEditEntry(res.data.esp32_entry_ip || '');
      setEditExit(res.data.esp32_exit_ip || '');
    } catch (e) {
      console.error('게이트 IP 로드 실패:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 매장 열쇠를 미리 받아 둔다 (직접 호출 폴백에서 씀). 실패해도 서버 경유는 그대로 된다.
  useEffect(() => {
    apiClient.get('/v1/gate/token')
      .then(res => setGateToken(res.data?.gate_token || ''))
      .catch(() => { /* 열쇠가 없으면 잠기지 않은 게이트 — 직접 호출도 그냥 열린다 */ });
  }, []);

  const callEsp32Open = async (ip: string, direction: 'entry' | 'exit') => {
    const target = ip.trim();
    if (!target) return false;
    try {
      const url = target.startsWith('http') ? `${target}/open` : `http://${target}/open`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(gateToken ? { 'X-Gate-Token': gateToken } : {}),
        },
        body: JSON.stringify({
          direction,
          ...(gateToken ? { token: gateToken } : {}),
          // 설정을 못 읽었을 때도 5초. 0.8초면 학생이 지나가기 전에 문이 닫힌다
          // (태블릿 쪽 폴백은 원래 5000 — 원장 화면만 짧게 열리고 있었다)
          duration_ms: settings?.esp32_open_duration_ms || 5000,
        }),
      });
      return res.ok;
    } catch (e) {
      console.error('ESP32 open 실패:', e);
      return false;
    }
  };

  const showToast = (text: string, color: string) => {
    const toast = document.createElement('div');
    toast.textContent = text;
    toast.style.cssText = `position:fixed;top:80px;right:20px;background:${color};color:white;padding:12px 20px;border-radius:8px;font-weight:600;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.2);`;
    document.body.appendChild(toast);
    setTimeout(() => { try { document.body.removeChild(toast); } catch { /* 이미 지워짐 */ } }, 1800);
  };

  /** 서버를 거쳐 우리 매장 게이트를 연다. 로그인한 계정의 매장만 열린다(서버가 판단). */
  const callServerOpen = async (direction: 'entry' | 'exit'): Promise<boolean> => {
    try {
      await apiClient.post('/v1/gate/remote-open', {
        direction,
        duration_ms: settings?.esp32_open_duration_ms || 5000,
        target_role: direction,
        reason: '출석부에서 열기',
      });
      return true;
    } catch (e: any) {
      console.warn('서버 경유 개방 실패:', e?.response?.status, e?.response?.data?.detail);
      return false;
    }
  };

  const handleOpen = async (direction: 'entry' | 'exit') => {
    setOpening(direction);

    // ① 서버 경유 — 어디서 눌러도 되고, HTTPS 페이지에서도 막히지 않는다
    let ok = await callServerOpen(direction);

    // ② 서버가 안 되면(인터넷 끊김 등) 매장 안에서 직접 부른다.
    //    HTTPS 페이지에서는 브라우저가 http:// 호출을 차단하므로 로컬 매장에서만 유효하다.
    if (!ok) {
      const entryIp = settings?.esp32_entry_ip;
      const exitIp = settings?.esp32_exit_ip;
      const targetIp = direction === 'exit' && exitIp ? exitIp : entryIp;
      if (targetIp) ok = await callEsp32Open(targetIp, direction);
    }

    setOpening(null);

    if (ok) {
      showToast(`🚪 ${direction === 'entry' ? '입구' : '출구'} 열림`, '#10B981');
      return;
    }

    // 여기까지 왔으면 두 길 다 실패다. 원인별로 다르게 안내한다.
    if (!settings?.esp32_entry_ip) {
      if (window.confirm('게이트를 열지 못했습니다.\n\n게이트가 아직 등록되지 않았을 수 있습니다. 설정 화면을 열까요?')) {
        setShowSettings(true);
      }
      return;
    }
    alert(
      '❌ 게이트를 열지 못했습니다.\n\n' +
      '• 게이트 전원과 인터넷(WiFi) 연결을 확인해주세요\n' +
      '• 태블릿 설정에서 게이트를 다시 등록하면 복구됩니다\n' +
      '• 매장 안 PC라면 게이트와 같은 WiFi 인지 확인해주세요'
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/v1/owner/gate-ip', {
        esp32_entry_ip: editEntry.trim(),
        esp32_exit_ip: editExit.trim(),
      });
      await load();
      setShowSettings(false);
    } catch (e: any) {
      alert(e?.response?.data?.detail || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  // 같은 네트워크에서 ESP32 검색
  const handleScan = async () => {
    setScanning(true);
    setScanFound([]);
    const found: string[] = [];
    // gate.local 우선
    try {
      const r = await fetch('http://gate.local/status', { method: 'GET' });
      if (r.ok) {
        const data = await r.json();
        if (data?.ok && data?.relay_pin !== undefined) found.push('gate.local');
      }
    } catch {}
    // 일반 대역 병렬 스캔
    const subnets = ['192.168.0', '192.168.1', '192.168.10', '192.168.50', '192.168.60', '192.168.100'];
    const tryIp = async (ip: string): Promise<boolean> => {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 800);
        const r = await fetch(`http://${ip}/status`, { signal: ctrl.signal });
        clearTimeout(t);
        if (r.ok) {
          const data = await r.json();
          return data?.ok && data?.relay_pin !== undefined;
        }
      } catch {}
      return false;
    };
    for (const subnet of subnets) {
      const batch = 50;
      for (let s = 1; s < 255; s += batch) {
        const ips = [];
        for (let i = s; i < Math.min(s + batch, 255); i++) ips.push(`${subnet}.${i}`);
        const results = await Promise.all(ips.map(ip => tryIp(ip).then(ok => ({ ip, ok }))));
        for (const r of results) if (r.ok && !found.includes(r.ip)) found.push(r.ip);
      }
    }
    setScanFound(found);
    setScanning(false);
    if (found.length === 0) {
      alert('네트워크에서 ESP32를 찾을 수 없습니다.\n수동으로 IP를 입력해주세요.');
    }
  };

  if (loading) return null;

  const hasEntry = !!settings?.esp32_entry_ip;
  const hasExit = !!settings?.esp32_exit_ip;

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleOpen('entry')}
          disabled={opening !== null}
          title={hasEntry ? '입구 게이트 열기' : '게이트 열기 (서버를 거쳐 우리 매장 게이트를 엽니다)'}
          className="px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
        >
          {opening === 'entry' ? '⏳' : '🚪'} {hasExit ? '입구 열기' : '게이트 열기'}
        </button>

        {hasExit && (
          <button
            onClick={() => handleOpen('exit')}
            disabled={opening !== null}
            title="출구 게이트 열기"
            className="px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white"
          >
            {opening === 'exit' ? '⏳' : '🚪'} 출구 열기
          </button>
        )}

        <button
          onClick={() => setShowSettings(true)}
          title="게이트 IP 설정"
          className="px-2 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 text-gray-700"
        >
          ⚙️
        </button>
      </div>

      {/* 설정 모달 */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              🚪 게이트 IP 설정
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              매장에 설치된 ESP32 IP를 입력하세요. 모든 운영자 PC에서 공유됩니다.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  메인 (입구 또는 입출구 통합) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editEntry}
                  onChange={(e) => setEditEntry(e.target.value)}
                  placeholder="192.168.0.70 또는 gate.local"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  출구 (분리형 매장만, 비워두면 메인이 양방향)
                </label>
                <input
                  type="text"
                  value={editExit}
                  onChange={(e) => setEditExit(e.target.value)}
                  placeholder="192.168.0.71 (선택)"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>

              <div className="border-t pt-3">
                <button
                  onClick={handleScan}
                  disabled={scanning}
                  className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded text-sm font-medium"
                >
                  {scanning ? '🔍 검색 중...' : '🔍 같은 네트워크에서 자동 검색'}
                </button>
                {scanFound.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs text-blue-800 mb-2">발견된 ESP32 ({scanFound.length}개) — 클릭하여 슬롯에 적용:</p>
                    <div className="flex flex-wrap gap-2">
                      {scanFound.map(ip => (
                        <div key={ip} className="flex gap-1">
                          <button
                            onClick={() => setEditEntry(ip)}
                            className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded hover:bg-green-200"
                          >
                            메인 ← {ip}
                          </button>
                          <button
                            onClick={() => setEditExit(ip)}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200"
                          >
                            출구 ← {ip}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => { setShowSettings(false); setScanFound([]); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-sm font-medium"
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GateOpenButton;
